import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDevices } from '@/features/devices';

import { supportKeys } from '../hooks';
import { useSupportStore } from '../store';

import { RaiseSupportTicketFlow } from './raise-support-ticket-flow';
import { SupportListingDrawer } from './support-listing-drawer';
import { SupportTicketDetailDrawer } from './support-ticket-detail-drawer';

/**
 * Top-level Customer Support overlay: the listing drawer, the detail drawer stacked on top,
 * and the (device-selectable) raise flow. Mounted once in <TopNav>. Reads/writes only the
 * drawer-state store; ticket data is owned by TanStack Query.
 */
export function CustomerSupportDrawers() {
  const { listOpen, openTicketId, openList, openTicket, closeTicket, closeAll } = useSupportStore();
  const queryClient = useQueryClient();
  const { data: devices } = useDevices();
  const [raiseOpen, setRaiseOpen] = useState(false);

  // Returning from a ticket refreshes the lists so status moves / new replies show
  // (story 101970 §9 — manual refresh; back-from-detail is the trigger).
  const handleCloseTicket = () => {
    closeTicket();
    void queryClient.invalidateQueries({ queryKey: supportKeys.list('active') });
    void queryClient.invalidateQueries({ queryKey: supportKeys.list('closed') });
    void queryClient.invalidateQueries({ queryKey: supportKeys.openCount() });
  };

  return (
    <>
      <SupportListingDrawer
        open={listOpen}
        onClose={closeAll}
        onOpenTicket={openTicket}
        onRaise={() => setRaiseOpen(true)}
      />

      <SupportTicketDetailDrawer
        open={openTicketId != null}
        ticketId={openTicketId}
        onClose={handleCloseTicket}
      />

      <RaiseSupportTicketFlow
        open={raiseOpen}
        devices={devices ?? []}
        onClose={() => {
          setRaiseOpen(false);
          // A new ticket lands in Active — refresh + ensure the listing is visible.
          void queryClient.invalidateQueries({ queryKey: supportKeys.list('active') });
          if (!listOpen) openList();
        }}
      />
    </>
  );
}
